import { tcSaveData, tcGetSaveData, tcDeleteData, isApp } from './bridge'

/**
 * 防抖定时器映射（用于减少对 Native 的频繁写入）
 */
const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>()

/**
 * 防抖延迟时间（毫秒）
 * 减少写入频率，避免短时间内多次调用 Native
 */
const DEBOUNCE_DELAY = 500

/**
 * 自定义 Storage 适配器（仅用于 App 环境）
 *
 * 使用场景：
 * - H5 环境：不使用此适配器，store 数据仅存储在内存中（SPA 特性，页面跳转不会丢失）
 * - App 环境：使用此适配器，将 store 数据持久化到 Native（支持跨 WebView 共享）
 *
 * 原因：App 中每个页面是独立的 WebView，store 内存状态不共享，
 * 因此需要将 store 数据持久化到 Native，实现跨 WebView 数据共享
 */
export const customStorage: Storage = {
  /**
   * 获取存储的值（同步方法）
   *
   * - App 环境：由于 bridge 是异步的，这里返回 localStorage 的临时缓存
   *   实际数据会在 store 初始化时通过 beforeHydrate 钩子异步加载
   */
  getItem(key: string): string | null {
    // App 环境：从 localStorage 读取临时缓存
    // 这里的 localStorage 只是临时缓存，真正的数据存储在 Native
    // 在 store 初始化时会通过 beforeHydrate 钩子从 Native 异步加载
    return localStorage.getItem(key)
  },

  /**
   * 设置存储的值
   *
   * App 环境：通过 bridge 存储到 Native（同时更新 localStorage 缓存）
   *
   * 工作流程：
   * 1. value 通常是 JSON 字符串（Pinia 序列化后的 store 数据）
   * 2. tcSaveData 会自动将 JSON 字符串解析为对象
   * 3. Native 存储的是结构化对象，不是嵌套字符串
   *
   * @example
   * setItem('user-store', '{"name":"张三","id":123}')
   * // Native 存储: { "user-store": { name: "张三", id: 123 } }
   */
  setItem(key: string, value: string): void {
    // 立即更新 localStorage 缓存（用于下次同步读取）
    localStorage.setItem(key, value)

    // 通过 bridge 存储到 Native（仅 App 环境）
    // 使用防抖机制，避免短时间内多次调用
    if (isApp()) {
      // 清除之前的定时器
      if (debounceTimers.has(key)) {
        clearTimeout(debounceTimers.get(key)!)
      }

      // 延迟写入 Native
      debounceTimers.set(key, setTimeout(() => {
        tcSaveData({ [key]: value })
        debounceTimers.delete(key)
      }, DEBOUNCE_DELAY))
    }
  },

  /**
   * 删除存储的值
   */
  removeItem(key: string): void {
    // 删除 localStorage 缓存
    localStorage.removeItem(key)

    // 清除待执行的防抖定时器（避免取消的写入又执行）
    if (debounceTimers.has(key)) {
      clearTimeout(debounceTimers.get(key)!)
      debounceTimers.delete(key)
    }

    // 通过 bridge 删除 Native 存储（仅 App 环境）
    if (isApp()) {
      tcDeleteData([key])
    }
  },
}

/**
 * 异步获取存储的值（用于 store 初始化时从 Native 加载数据）
 *
 * @param key store 的 key（如 'user-store'）
 * @returns JSON 字符串或 null
 *
 * 工作流程：
 * 1. Native 存储的是对象格式：{ "user-store": { name: "张三" } }
 * 2. tcGetSaveData 会自动将对象转为 JSON 字符串
 * 3. 返回 '{"name":"张三"}' 供 Pinia 反序列化
 *
 * @example
 * const data = await getStorageItem('user-store')
 * // Native 存储: { name: "张三", id: 123 }
 * // 返回: '{"name":"张三","id":123}'
 */
export async function getStorageItem(key: string): Promise<string | null> {
  if (isApp()) {
    const result = await tcGetSaveData(key)
    return result[key] ?? null
  }
  return null
}


